import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Shield, Link as LinkIcon, Copy, Check, XCircle, Clock, UserCheck, Bell, Smartphone,
} from "lucide-react";
import { toast } from "sonner";

/** Production-house "Grant Override" console.
 *
 *  Two on-ramps:
 *   1. Generate an invite link for someone NOT YET on the platform.
 *      They claim the slot via /invite/:token → auto-account + override applied.
 *   2. Directly grant an override to an existing user_id (already on platform).
 *
 *  Year-1 reality: option (1) is the dominant flow. As the ecosystem grows,
 *  option (2) takes over because everyone already has an iCard.
 */
export default function GrantOverride() {
  const { user } = useAuth();
  const isProductionHouse = user?.account_type === "production_house";

  const [projects, setProjects] = useState([]);
  const [roles, setRoles] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [invites, setInvites] = useState([]);
  const [copiedToken, setCopiedToken] = useState("");

  // Invite form state
  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const plus30ISO = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().slice(0, 10);
  }, []);
  const [form, setForm] = useState({
    project_id: "",
    role: "",
    temporary_association_id: "",
    starts_at: todayISO,
    expires_at: plus30ISO,
    invitee_name: "",
    invitee_email: "",
    invitee_phone: "",
    daily_rate: "",
  });

  // Direct grant state
  const [direct, setDirect] = useState({
    user_id: "",
    temporary_association_id: "",
    starts_at: todayISO,
    expires_at: plus30ISO,
  });

  // Push-self-test state
  const [pushStatus, setPushStatus]   = useState(null);   // {mode, fcm_live, apns_live, queue}
  const [pushTesting, setPushTesting] = useState(false);
  const [pushResult, setPushResult]   = useState(null);   // {device_count, results}

  const refreshPushStatus = async () => {
    try {
      const { data } = await api.get("/v1/push-worker/status");
      setPushStatus(data);
    } catch (err) {
      console.warn("[GrantOverride] push-worker/status failed:", err?.message);
    }
  };

  const runTestPush = async () => {
    setPushTesting(true);
    setPushResult(null);
    try {
      const { data } = await api.post("/v1/push-worker/test-push");
      setPushResult(data);
      if (data.device_count === 0) {
        toast.info("No devices registered yet — open the Expo app first");
      } else {
        const ok = data.results.filter((r) => r.ok).length;
        toast.success(`Sent to ${ok}/${data.device_count} device(s)`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test push failed");
    } finally {
      setPushTesting(false);
    }
  };

  const refreshInvites = async () => {
    try {
      const { data } = await api.get("/v1/override-invites");
      setInvites(data.items || []);
    } catch (err) {
      console.warn("[GrantOverride] override-invites list failed:", err?.message);
    }
  };

  useEffect(() => {
    if (!isProductionHouse) return;
    (async () => {
      const [p, r, a] = await Promise.all([
        api.get("/projects").then((x) => x.data)
           .catch((err) => { console.warn("[GrantOverride] /projects failed:", err?.message); return []; }),
        api.get("/meta/roles").then((x) => x.data)
           .catch((err) => { console.warn("[GrantOverride] /meta/roles failed:", err?.message); return []; }),
        api.get("/meta/associations").then((x) => x.data)
           .catch((err) => { console.warn("[GrantOverride] /meta/associations failed:", err?.message); return []; }),
      ]);
      setProjects(p);
      setRoles(r);
      // The endpoint returns plain array when no geo_state is passed.
      setAssociations(Array.isArray(a) ? a : (a.local || []).concat(a.others || []));
      await refreshInvites();
      await refreshPushStatus();
    })();
  }, [isProductionHouse]);

  if (!isProductionHouse) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-16" data-testid="override-locked">
        <div className="cs-card rounded-2xl p-10 text-center">
          <Shield size={28} className="mx-auto text-white/40 mb-4" />
          <h1 className="font-display text-2xl uppercase tracking-tight">
            Production House Only
          </h1>
          <p className="mt-3 text-sm text-white/60">
            Grant Override is restricted to production-house accounts. It lets you
            bring an out-of-state or non-member professional onto a single project
            without forcing them to register with your local association first.
          </p>
        </div>
      </div>
    );
  }

  const submitInvite = async (e) => {
    e.preventDefault();
    if (!form.project_id || !form.role || !form.temporary_association_id || !form.invitee_name) {
      toast.error("Project, role, association and invitee name are required");
      return;
    }
    try {
      const payload = {
        ...form,
        starts_at:  toISODate(form.starts_at),
        expires_at: toISODate(form.expires_at, true),
        daily_rate: form.daily_rate ? Number(form.daily_rate) : 0,
      };
      const { data } = await api.post("/v1/override-invites", payload);
      toast.success("Invite link generated");
      // Auto-copy URL for convenience
      copy(data.invite_url, data.token);
      // Fully reset invitee + rate fields so the form is unambiguously empty
      // for the next invite (project/role/assoc/dates stay so a PA can
      // batch-issue links to multiple people for the same shoot).
      setForm((f) => ({
        ...f, invitee_name: "", invitee_email: "", invitee_phone: "", daily_rate: "",
      }));
      await refreshInvites();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const submitDirect = async (e) => {
    e.preventDefault();
    if (!direct.user_id || !direct.temporary_association_id) {
      toast.error("user_id and association are required");
      return;
    }
    try {
      await api.post("/v1/user/project-override", {
        ...direct,
        starts_at:  toISODate(direct.starts_at),
        expires_at: toISODate(direct.expires_at, true),
      });
      toast.success("Override granted to existing user");
      setDirect({
        user_id: "", temporary_association_id: "",
        starts_at: todayISO, expires_at: plus30ISO,
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const revoke = async (id) => {
    try {
      await api.post(`/v1/override-invites/${id}/revoke`);
      toast.success("Invite revoked");
      await refreshInvites();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const copy = (url, token) => {
    // Build absolute URL if backend returned a relative one
    const finalUrl = url?.startsWith("http") ? url : `${window.location.origin}${url}`;
    navigator.clipboard.writeText(finalUrl).then(() => {
      setCopiedToken(token);
      toast.success("Invite link copied to clipboard");
      setTimeout(() => setCopiedToken(""), 2500);
    }).catch(() => toast.error("Could not copy. Select and copy manually."));
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10" data-testid="grant-override-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Production Console</div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
          Grant <span style={{ color: "var(--association-theme)" }}>Override</span>
        </h1>
        <p className="mt-3 text-sm text-white/60 max-w-2xl">
          Issue a tokenized invite link to onboard a professional onto a project
          under a temporary association — interstate crossovers, vendor-side
          specialists, or out-of-network talent. The platform auto-creates the
          account when they claim the link.
        </p>
      </div>

      {/* ─────────────── Push self-test (production-house only) ─────────────── */}
      <div className="cs-card rounded-2xl p-5 mb-8" data-testid="push-test-card">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">
              <Bell size={16} className="text-white/80" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] tracking-[0.25em] uppercase text-white/55">
                  Push Notifications
                </span>
                {pushStatus && <PushModePill mode={pushStatus.mode} />}
              </div>
              <div className="mt-1 text-sm text-white/80">
                {pushStatus
                  ? <>FCM <Dot ok={pushStatus.fcm_live} /> {pushStatus.fcm_live ? "live" : "mocked"}
                     <span className="text-white/30 mx-2">·</span>
                     APNs <Dot ok={pushStatus.apns_live} /> {pushStatus.apns_live ? "live" : "mocked"}
                     <span className="text-white/30 mx-2">·</span>
                     Queue: {pushStatus.queue?.pending || 0} pending / {pushStatus.queue?.delivered || 0} delivered
                    </>
                  : <span className="text-white/40">Loading worker status…</span>}
              </div>
            </div>
          </div>
          <Button
            type="button" onClick={runTestPush} disabled={pushTesting}
            className="h-10 px-5 rounded-full text-[10px] uppercase tracking-[0.25em] bg-white/10 hover:bg-white/15 text-white shrink-0"
            data-testid="test-push-btn"
          >
            <Smartphone size={12} className="mr-2" />
            {pushTesting ? "Sending…" : "Send Test Push"}
          </Button>
        </div>

        {pushResult && (
          <div className="mt-4 pt-4 border-t border-white/10" data-testid="push-test-result">
            {pushResult.device_count === 0 ? (
              <div className="text-xs text-white/55">
                No devices registered yet. Open the Expo app + sign in once so
                its FCM/APNs token reaches <code className="text-white/70">/v1/device-tokens</code>,
                then click again.
              </div>
            ) : (
              <div className="space-y-1.5">
                {pushResult.results.map((r, i) => (
                  <div key={`${r.platform}-${r.token_prefix || i}`} className="flex items-center gap-2 text-[11px] font-mono text-white/70" data-testid={`push-result-row-${i}`}>
                    <Dot ok={r.ok} />
                    <span className="uppercase tracking-[0.18em] text-white/60 w-20">{r.platform}</span>
                    <span className="text-white/40">{r.token_prefix}</span>
                    <span className="text-white/30 px-1">·</span>
                    <span className={r.ok ? "text-emerald-300" : "text-red-300"}>
                      {r.ok ? r.mode : (r.reason || "failed")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>


      <Tabs defaultValue="invite">
        <TabsList className="bg-white/5 border border-white/10" data-testid="grant-tabs">
          <TabsTrigger value="invite" data-testid="tab-invite">
            <LinkIcon size={13} className="mr-2" /> Invite Link
          </TabsTrigger>
          <TabsTrigger value="direct" data-testid="tab-direct">
            <UserCheck size={13} className="mr-2" /> Direct Grant
          </TabsTrigger>
          <TabsTrigger value="list" data-testid="tab-list">
            <Clock size={13} className="mr-2" /> Active &amp; History ({invites.length})
          </TabsTrigger>
        </TabsList>

        {/* ---------------------------- INVITE LINK ----------------------------- */}
        <TabsContent value="invite" className="mt-6">
          <form
            onSubmit={submitInvite}
            className="cs-card rounded-2xl p-6 grid md:grid-cols-2 gap-5"
            data-testid="invite-form"
          >
            <Field label="Project" tid="invite-project-field">
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="invite-project" className="h-11 bg-black/40 border-white/15">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.length === 0 && (
                    <SelectItem value="__none" disabled>No projects yet</SelectItem>
                  )}
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Role on this project" tid="invite-role-field">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="invite-role" className="h-11 bg-black/40 border-white/15">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Temporary association" tid="invite-assoc-field">
              <Select
                value={form.temporary_association_id}
                onValueChange={(v) => setForm({ ...form, temporary_association_id: v })}
              >
                <SelectTrigger data-testid="invite-assoc" className="h-11 bg-black/40 border-white/15">
                  <SelectValue placeholder="Select association" />
                </SelectTrigger>
                <SelectContent>
                  {associations.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name} · {a.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Daily rate (₹)" tid="invite-rate-field">
              <Input
                type="number" min={0} value={form.daily_rate}
                onChange={(e) => setForm({ ...form, daily_rate: e.target.value })}
                placeholder="0" data-testid="invite-rate"
                className="h-11 bg-black/40 border-white/15"
              />
            </Field>

            <Field label="Starts at" tid="invite-starts-field">
              <Input
                type="date" value={form.starts_at}
                onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                data-testid="invite-starts"
                className="h-11 bg-black/40 border-white/15"
              />
            </Field>

            <Field label="Expires at" tid="invite-expires-field">
              <Input
                type="date" value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                data-testid="invite-expires"
                className="h-11 bg-black/40 border-white/15"
              />
            </Field>

            <Field label="Invitee name" tid="invite-name-field">
              <Input
                value={form.invitee_name}
                onChange={(e) => setForm({ ...form, invitee_name: e.target.value })}
                placeholder="As they should appear on the call sheet"
                data-testid="invite-name"
                className="h-11 bg-black/40 border-white/15"
              />
            </Field>

            <Field label="Invitee email (optional)" tid="invite-email-field">
              <Input
                type="email" value={form.invitee_email}
                onChange={(e) => setForm({ ...form, invitee_email: e.target.value })}
                placeholder="prefilled on the claim page"
                data-testid="invite-email"
                className="h-11 bg-black/40 border-white/15"
              />
            </Field>

            <Field label="Invitee phone (optional)" tid="invite-phone-field">
              <Input
                value={form.invitee_phone}
                onChange={(e) => setForm({ ...form, invitee_phone: e.target.value })}
                placeholder="+91…"
                data-testid="invite-phone"
                className="h-11 bg-black/40 border-white/15"
              />
            </Field>

            <div className="md:col-span-2 flex justify-end pt-2">
              <Button
                type="submit"
                className="btn-themed h-12 px-8 rounded-full text-xs uppercase tracking-[0.25em] font-semibold"
                data-testid="invite-submit"
              >
                Generate Invite Link →
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* ---------------------------- DIRECT GRANT ---------------------------- */}
        <TabsContent value="direct" className="mt-6">
          <form
            onSubmit={submitDirect}
            className="cs-card rounded-2xl p-6 grid md:grid-cols-2 gap-5"
            data-testid="direct-form"
          >
            <Field label="User ID (existing platform user)" tid="direct-uid-field">
              <Input
                value={direct.user_id}
                onChange={(e) => setDirect({ ...direct, user_id: e.target.value })}
                placeholder="e.g. b8a32c…"
                data-testid="direct-uid"
                className="h-11 bg-black/40 border-white/15 font-mono text-xs"
              />
            </Field>

            <Field label="Temporary association" tid="direct-assoc-field">
              <Select
                value={direct.temporary_association_id}
                onValueChange={(v) => setDirect({ ...direct, temporary_association_id: v })}
              >
                <SelectTrigger data-testid="direct-assoc" className="h-11 bg-black/40 border-white/15">
                  <SelectValue placeholder="Select association" />
                </SelectTrigger>
                <SelectContent>
                  {associations.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} · {a.city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field label="Starts at" tid="direct-starts-field">
              <Input
                type="date" value={direct.starts_at}
                onChange={(e) => setDirect({ ...direct, starts_at: e.target.value })}
                data-testid="direct-starts"
                className="h-11 bg-black/40 border-white/15"
              />
            </Field>

            <Field label="Expires at" tid="direct-expires-field">
              <Input
                type="date" value={direct.expires_at}
                onChange={(e) => setDirect({ ...direct, expires_at: e.target.value })}
                data-testid="direct-expires"
                className="h-11 bg-black/40 border-white/15"
              />
            </Field>

            <div className="md:col-span-2 flex justify-end pt-2">
              <Button
                type="submit"
                className="btn-themed h-12 px-8 rounded-full text-xs uppercase tracking-[0.25em] font-semibold"
                data-testid="direct-submit"
              >
                Apply Override →
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* ----------------------------- LIST ----------------------------------- */}
        <TabsContent value="list" className="mt-6">
          {invites.length === 0 ? (
            <div className="cs-card rounded-2xl p-10 text-center text-sm text-white/60" data-testid="invites-empty">
              No invites issued yet. Generate one from the Invite Link tab.
            </div>
          ) : (
            <div className="space-y-3" data-testid="invites-list">
              {invites.map((inv) => (
                <div key={inv.id}
                  className="cs-card rounded-xl p-5 flex flex-col lg:flex-row gap-4 lg:items-center justify-between"
                  data-testid={`invite-row-${inv.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <StatusPill status={inv.status} />
                      <span className="text-[10px] tracking-[0.2em] uppercase text-white/45">
                        {inv.role_label} · {inv.project_title}
                      </span>
                    </div>
                    <div className="text-sm font-display uppercase tracking-tight">
                      {inv.invitee_name}{inv.invitee_email ? ` · ${inv.invitee_email}` : ""}
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-white/55">
                      <span
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full"
                        style={{ background: `${inv.temporary_association.color}25`, color: inv.temporary_association.color }}
                      >
                        {inv.temporary_association.short_name}
                      </span>
                      <span>· expires {fmtDate(inv.expires_at)}</span>
                    </div>
                    {inv.invite_url && (
                      <div className="mt-3 text-[11px] font-mono text-white/40 truncate">
                        {inv.invite_url}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {inv.status === "pending" && (
                      <>
                        <Button
                          type="button" onClick={() => copy(inv.invite_url, inv.token)}
                          className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.2em] bg-white/10 hover:bg-white/15 text-white"
                          data-testid={`copy-invite-${inv.id}`}
                        >
                          {copiedToken === inv.token ? <Check size={12} className="mr-1.5" /> : <Copy size={12} className="mr-1.5" />}
                          {copiedToken === inv.token ? "Copied" : "Copy Link"}
                        </Button>
                        <Button
                          type="button" onClick={() => revoke(inv.id)}
                          className="h-9 px-4 rounded-full text-[10px] uppercase tracking-[0.2em] bg-red-500/15 hover:bg-red-500/25 text-red-300"
                          data-testid={`revoke-invite-${inv.id}`}
                        >
                          <XCircle size={12} className="mr-1.5" /> Revoke
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Field({ label, children, tid }) {
  return (
    <div data-testid={tid}>
      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending:  { bg: "bg-amber-500/15",  fg: "text-amber-300",  label: "Pending"  },
    accepted: { bg: "bg-emerald-500/15", fg: "text-emerald-300", label: "Accepted" },
    expired:  { bg: "bg-white/10",       fg: "text-white/50",    label: "Expired"  },
    revoked:  { bg: "bg-red-500/15",     fg: "text-red-300",     label: "Revoked"  },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.2em] ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}

function PushModePill({ mode }) {
  const map = {
    live:         { bg: "bg-emerald-500/15", fg: "text-emerald-300", label: "Live"          },
    partial_live: { bg: "bg-amber-500/15",   fg: "text-amber-300",   label: "Partial Live"  },
    mocked:       { bg: "bg-white/10",       fg: "text-white/55",    label: "Mocked"        },
  };
  const s = map[mode] || map.mocked;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.25em] ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}

function Dot({ ok }) {
  return (
    <span
      className="inline-block w-1.5 h-1.5 rounded-full mx-1 align-middle"
      style={{ background: ok ? "#22c55e" : "#9ca3af" }}
    />
  );
}

// HTML <input type="date"> gives YYYY-MM-DD. Normalize to ISO datetime.
// When `endOfDay` is true, anchor to 23:59:59 so a same-day expiry is valid.
function toISODate(d, endOfDay = false) {
  if (!d) return "";
  const t = endOfDay ? "T23:59:59+00:00" : "T00:00:00+00:00";
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}${t}` : d;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined,
      { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}
